import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { requireAnyPermission } from '@/lib/permissions/permissions'
import { Button } from '@/components/ui/button'
import { useEffect, useMemo, useState } from 'react'
import { Client, createClientColumns } from '@/components/client/clientClolumns'
import NewClientDialog from '@/components/client/newClientDialog'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import { RxPlus } from 'react-icons/rx'
import { Laptop, Shuffle, Users } from 'lucide-react'
import ClientLoadingState from '@/components/client/ClientLoadingState'
import ClientRowSkeleton from '@/components/client/ClientRowSkeleton'
import { toast } from 'sonner'
import DeleteAlert from '@/components/DeleteAlert'
import {
  useHasPermission,
  useOrg,
  useRoleKey,
  useUserId,
  useHasTeamMembers,
} from '@/lib/states/session/hooks'
import { AssignCoachDialog } from '@/components/client/AssignCoachDialog'
import { ClientBulkActionBar } from '@/components/client/ClientBulkActionBar'
import { AnnouncementDialog } from '@/components/chat/AnnouncementDialog'
import { TrainingTemplateLinkDialog } from '@/components/templates/TrainingTemplateLinkDialog'
import {
  MealPlanTemplateLinkDialog,
  type MealPlanTemplateSummary,
} from '@/components/templates/MealPlanTemplateLinkDialog'
import { useSupabaseSession } from '@/lib/supabase/supabaseProvider'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { store } from '@/lib/states/store'
import { patchListCache, patchQueryCache } from '@trainrr/api-client'
import { createConversationWithClient } from '@/lib/utils/conversationUtils'
import { useBreakpoint } from '@/lib/hooks/useBreakpoint'
import { errorToast } from '@/lib/utils/ToastUtils'
import { cn } from '@/lib/utils/ui'
import { format, startOfDay } from 'date-fns'
import type { RowSelectionState } from '@tanstack/react-table'

const EMPTY_LINKED_PLAN_IDS = new Set<string>()

export const Route = createFileRoute('/_auth/_dashboard/clients/')({
  beforeLoad: () => {
    requireAnyPermission(['clients.read_all', 'clients.read_assigned'])
  },
  loader: () => {
    if (!store.getState().session.org?.id) {
      return
    }
    void trpcUtils.clients.list.prefetch()
  },
  component: ClientList,
  pendingComponent: () => <ClientLoadingState />,
})

function clientDisplayName(client: Client) {
  return (
    `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() ||
    'this client'
  )
}

function isConflictError(error: unknown) {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    (error as { data?: { code?: string } }).data?.code === 'CONFLICT'
  ) {
    return true
  }
  const message = error instanceof Error ? error.message : ''
  return /already exists|duplicate|conflict/i.test(message)
}

function ClientList() {
  const [open, setOpen] = useState(false)
  const [clientsToArchive, setClientsToArchive] = useState<Client[]>([])
  const [assignClient, setAssignClient] = useState<Client | null>(null)
  const [bulkCoachClientIds, setBulkCoachClientIds] = useState<string[]>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [announcementOpen, setAnnouncementOpen] = useState(false)
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false)
  const [mealPlanDialogOpen, setMealPlanDialogOpen] = useState(false)

  const navigate = useNavigate()
  const { isBelowSm } = useBreakpoint('sm')

  const org = useOrg()
  const canAssignCoaches = useHasPermission('clients.assign_coaches')
  const canAssignTraining = useHasPermission('plans.write')
  const canMessage = useHasPermission('messages.read')
  const currentUserId = useUserId()
  const currentUserRoleKey = useRoleKey()
  const hasTeamMembers = useHasTeamMembers()
  const { session } = useSupabaseSession()
  const { data: entitlements } = useOrgEntitlements()
  const coachEmail = session?.user?.email ?? 'your email'
  const clientLimit = entitlements?.max_clients ?? null
  const clientCount = entitlements?.client_count ?? 0
  const orgId = org?.id ?? ''

  const utils = trpc.useUtils()
  const archiveClient = trpc.clients.archive.useMutation()
  const assignTraining = trpc.training.assignToClient.useMutation()
  const assignMealPlan = trpc.nutrition.assignToClient.useMutation()
  const {
    data: clients,
    error,
    isLoading,
  } = trpc.clients.list.useQuery(undefined, { enabled: Boolean(orgId) })
  if (error) {
    console.error('error fetching clients', error)
  }

  const { data: trainingTemplates } = trpc.training.listTemplates.useQuery(
    undefined,
    {
      enabled: Boolean(orgId) && trainingDialogOpen && canAssignTraining,
    },
  )
  const { data: assignableMealTemplates } =
    trpc.nutrition.listAssignableTemplates.useQuery(undefined, {
      enabled: Boolean(orgId) && mealPlanDialogOpen,
    })

  const clientsWithCoaches = (clients as Client[] | undefined) ?? []
  const coachFilterOptions = useMemo(() => {
    const byId = new Map<
      string,
      {
        label: string
        value: string
        avatarUrl?: string | null
        avatarFallback: string
      }
    >()
    for (const client of clientsWithCoaches) {
      for (const row of client.client_coaches ?? []) {
        const coach = row.coach
        if (!coach?.user_id || byId.has(coach.user_id)) continue
        const name =
          `${coach.first_name ?? ''} ${coach.last_name ?? ''}`.trim() ||
          coach.email ||
          'Coach'
        const avatarFallback = `${coach.first_name?.[0] ?? 'C'}${
          coach.last_name?.[0] ?? ''
        }`
        byId.set(coach.user_id, {
          label: name,
          value: coach.user_id,
          avatarUrl: coach.avatar?.url ?? null,
          avatarFallback,
        })
      }
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [clientsWithCoaches])
  const selectedClients = useMemo(
    () => clientsWithCoaches.filter((client) => rowSelection[client.id]),
    [clientsWithCoaches, rowSelection],
  )
  const selectedCount = selectedClients.length

  useEffect(() => {
    if (selectedCount === 0) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      setRowSelection({})
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectedCount])
  const activeSelectedClients = useMemo(
    () => selectedClients.filter((client) => client.status !== 'ARCHIVED'),
    [selectedClients],
  )
  const announcementClients = useMemo(
    () => clientsWithCoaches.filter((client) => client.status !== 'ARCHIVED'),
    [clientsWithCoaches],
  )
  const announcementSelectedIds = useMemo(
    () => activeSelectedClients.map((client) => client.id),
    [activeSelectedClients],
  )
  const mealPlanTemplates = useMemo<MealPlanTemplateSummary[]>(
    () =>
      (assignableMealTemplates ?? []).map((template) => ({
        id: template.id,
        name: template.name,
        org_id: template.org_id,
        is_template: template.is_template,
        meals: template.meals as MealPlanTemplateSummary['meals'],
      })),
    [assignableMealTemplates],
  )

  const canArchiveClient = (client: Client) => {
    if (client.status === 'ARCHIVED') return false
    const isAdminOwner =
      currentUserRoleKey === 'owner' || currentUserRoleKey === 'admin'
    const isAssignedCoach =
      !!currentUserId &&
      (client.client_coaches ?? []).some(
        (row) => row.coach?.user_id === currentUserId,
      )
    return isAdminOwner || isAssignedCoach
  }
  const archivableSelected = selectedClients.filter(canArchiveClient)
  const assignCoachClientIds = assignClient
    ? [assignClient.id]
    : bulkCoachClientIds

  const columns = useMemo(
    () =>
      createClientColumns({
        canAssignCoaches,
        canMessage,
        coachEmail,
        currentUserId,
        currentUserRoleKey,
        showCoachesColumn: hasTeamMembers,
      }),
    [
      canAssignCoaches,
      canMessage,
      coachEmail,
      currentUserId,
      currentUserRoleKey,
      hasTeamMembers,
    ],
  )

  const handleOnClickEdit = (client: Client) => {
    if (open) {
      return
    }

    navigate({ to: '/clients/$clientId', params: { clientId: client.id } })
  }

  const handleMessageClient = async (client: Client) => {
    try {
      const conversationId = await createConversationWithClient({
        clientId: client.id,
      })
      await navigate({
        to: '/chat',
        search: { conversationId },
      })
    } catch (error) {
      console.error('Failed to start conversation', error)
      toast.error('Failed to start conversation')
    }
  }

  const handleOpenChange = (open: boolean) => {
    setOpen(open)
    if (!open) {
      void utils.clients.list.invalidate()
    }
  }

  const handleArchiveClients = async (targets: Client[]) => {
    let succeeded = 0
    for (const client of targets) {
      try {
        const updated = await archiveClient.mutateAsync({
          clientId: client.id,
        })
        patchListCache(utils.clients.list, (old) =>
          old.map((row) =>
            row.id === updated.id
              ? {
                  ...row,
                  status: updated.status,
                  archived_at: updated.archived_at,
                }
              : row,
          ),
        )
        patchQueryCache(utils.clients.get, { clientId: client.id }, (old) => ({
          ...old,
          status: updated.status,
          archived_at: updated.archived_at,
        }))
        succeeded += 1
      } catch (error) {
        console.error('Failed to archive client', error)
      }
    }
    if (succeeded > 0) {
      toast.success(
        succeeded === 1 ? 'Client archived' : `${succeeded} clients archived`,
      )
      void utils.billing.entitlements.invalidate()
      setRowSelection({})
    }
    if (succeeded < targets.length) {
      toast.error(
        targets.length === 1
          ? 'Failed to archive client'
          : 'Failed to archive some clients',
      )
    }
  }

  const handleLinkTrainingTemplate = async (
    template: { id: string },
    startDate?: string,
  ) => {
    const targets = selectedClients.filter(
      (client) => client.status !== 'ARCHIVED',
    )
    if (!template.id || targets.length === 0) return false
    const normalizedStartDate =
      startDate && startDate.length > 0
        ? startDate
        : format(startOfDay(new Date()), 'yyyy-MM-dd')

    const results = await Promise.allSettled(
      targets.map((client) =>
        assignTraining.mutateAsync({
          clientId: client.id,
          planId: template.id,
          startDate: normalizedStartDate,
          isVisible: true,
        }),
      ),
    )

    let linked = 0
    let alreadyLinked = 0
    let failed = 0
    let lastError: unknown
    for (const result of results) {
      if (result.status === 'fulfilled') {
        linked += 1
        continue
      }
      if (isConflictError(result.reason)) {
        alreadyLinked += 1
        continue
      }
      failed += 1
      lastError = result.reason
    }

    if (linked > 0) {
      toast.success(
        linked === 1
          ? 'Training plan assigned to 1 client'
          : `Training plan assigned to ${linked} clients`,
      )
    }
    if (alreadyLinked > 0) {
      toast.info(
        alreadyLinked === 1
          ? 'Template is already linked to 1 client'
          : `Template is already linked to ${alreadyLinked} clients`,
      )
    }
    if (failed > 0) {
      console.error('Error linking training template', lastError)
      errorToast(lastError instanceof Error ? lastError : null)
    }

    return failed === 0
  }

  const handleLinkMealPlanTemplate = async (
    template: MealPlanTemplateSummary,
  ) => {
    const targets = selectedClients.filter(
      (client) => client.status !== 'ARCHIVED',
    )
    if (!template.id || targets.length === 0) return

    const results = await Promise.allSettled(
      targets.map((client) =>
        assignMealPlan.mutateAsync({
          clientId: client.id,
          planId: template.id,
          isVisible: true,
        }),
      ),
    )

    let linked = 0
    let alreadyLinked = 0
    let failed = 0
    let lastError: unknown
    for (const result of results) {
      if (result.status === 'fulfilled') {
        linked += 1
        continue
      }
      if (isConflictError(result.reason)) {
        alreadyLinked += 1
        continue
      }
      failed += 1
      lastError = result.reason
    }

    if (linked > 0) {
      toast.success(
        linked === 1
          ? 'Meal plan assigned to 1 client'
          : `Meal plan assigned to ${linked} clients`,
      )
    }
    if (alreadyLinked > 0) {
      toast.info(
        alreadyLinked === 1
          ? 'Template is already linked to 1 client'
          : `Template is already linked to ${alreadyLinked} clients`,
      )
    }
    if (failed > 0) {
      console.error('Error linking meal plan template', lastError)
      errorToast(lastError instanceof Error ? lastError : null)
      return
    }

    setMealPlanDialogOpen(false)
  }

  const archiveTitle =
    clientsToArchive.length === 1
      ? `Archive ${clientDisplayName(clientsToArchive[0])}?`
      : clientsToArchive.length > 1
        ? `Archive ${clientsToArchive.length} clients?`
        : 'Archive client?'
  const archiveDescription =
    clientsToArchive.length === 1
      ? `Archiving ${clientDisplayName(clientsToArchive[0])} will remove their access but keep their records.`
      : clientsToArchive.length > 1
        ? `Archiving these ${clientsToArchive.length} clients will remove their access but keep their records.`
        : 'Archiving this client will remove their access but keep their records.'

  return (
    <>
      <DeleteAlert
        title={archiveTitle}
        description={archiveDescription}
        open={clientsToArchive.length > 0}
        onCancel={() => setClientsToArchive([])}
        confirmText="Archive"
        onDelete={async () => {
          if (clientsToArchive.length === 0) return
          await handleArchiveClients(clientsToArchive)
          setClientsToArchive([])
        }}
      />
      <NewClientDialog open={open} onOpenChange={handleOpenChange} />
      <AssignCoachDialog
        clientIds={assignCoachClientIds}
        open={assignCoachClientIds.length > 0}
        canAssign={canAssignCoaches}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setAssignClient(null)
            setBulkCoachClientIds([])
          }
        }}
      />
      <AnnouncementDialog
        open={announcementOpen}
        onOpenChange={setAnnouncementOpen}
        clients={announcementClients}
        initialSelectedClientIds={announcementSelectedIds}
      />
      <TrainingTemplateLinkDialog
        open={trainingDialogOpen}
        onOpenChange={setTrainingDialogOpen}
        templatePlans={trainingTemplates ?? []}
        linkedPlanIds={EMPTY_LINKED_PLAN_IDS}
        onLinkTemplate={handleLinkTrainingTemplate}
      />
      <MealPlanTemplateLinkDialog
        open={mealPlanDialogOpen}
        onOpenChange={setMealPlanDialogOpen}
        templates={mealPlanTemplates}
        linkedPlanIds={EMPTY_LINKED_PLAN_IDS}
        onLinkTemplate={handleLinkMealPlanTemplate}
      />
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 pt-6 pb-0 md:pt-8">
            <div className="flex items-center justify-between gap-2 px-4 md:px-8">
              <h2 className="text-3xl font-bold tracking-tight">Clients</h2>
              <div className="flex items-center gap-3">
                {clientLimit !== null ? (
                  <p className="text-sm tabular-nums text-muted-foreground">
                    {clientCount}/{clientLimit}
                  </p>
                ) : null}
                <Button className="gap-2" onClick={() => setOpen(true)}>
                  <RxPlus /> Add New
                </Button>
              </div>
            </div>
            <div className="relative flex min-h-0 flex-1 flex-col">
              <VirtualizedDataTable
              className="min-h-0 flex-1"
              toolbarClassName="px-4 md:px-8"
              columns={columns}
              data={clientsWithCoaches}
              onRowSelected={handleOnClickEdit}
              onRowArchived={(client) => setClientsToArchive([client])}
              onAssignCoaches={
                canAssignCoaches
                  ? (client) => setAssignClient(client)
                  : undefined
              }
              onMessageClient={canMessage ? handleMessageClient : undefined}
              enableRowSelection
              getRowId={(client) => client.id}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              selectedRowClassName={cn(
                'data-[state=selected]:bg-blue-500/15',
                'data-[state=selected]:shadow-[inset_0_0_0_1px_rgb(59_130_246_/_0.45)]',
                '[@media(any-hover:hover)]:data-[state=selected]:hover:bg-blue-500/20',
              )}
              selectedCellClassName="group-data-[state=selected]:bg-blue-500/15"
              isLoading={isLoading}
              skeletonRow={<ClientRowSkeleton />}
              showSearchBar
              inlineFilters
              toggleFilter={{
                label: 'Show Archived',
                column: 'status',
                filterValue: 'ARCHIVED',
                defaultFilterOn: true,
                invertToggle: true,
              }}
              filterOptions={[
                {
                  column: 'client_category',
                  title: 'Category',
                  singleSelect: true,
                  includeAllOption: true,
                  allLabel: 'All categories',
                  options: [
                    { label: 'Online', value: 'online', icon: Laptop },
                    { label: 'In-Person', value: 'in-person', icon: Users },
                    { label: 'Hybrid', value: 'hybrid', icon: Shuffle },
                  ],
                },
                ...(hasTeamMembers && coachFilterOptions.length > 0
                  ? [
                      {
                        column: 'coaches',
                        title: 'Coaches',
                        allLabel: 'All coaches',
                        stackSelected: true,
                        options: coachFilterOptions,
                      },
                    ]
                  : []),
              ]}
              stickyHeader
              tableHeaderClassName="max-sm:hidden"
              tableClassName={cn(
                '[&_td]:py-2',
                '[&_tr[data-state=selected]]:bg-blue-500/15',
                '[&_tr[data-state=selected]:hover]:bg-blue-500/20',
              )}
              tableContainerClassName="min-h-0 flex-1 rounded-none"
              tableWrapperClassName="h-full min-h-0"
              tableViewportClassName="px-4 md:px-8"
              virtualizedHeight="100%"
              estimateRowHeight={isBelowSm ? 72 : 52}
            />
            <ClientBulkActionBar
              selectedCount={selectedCount}
              onClear={() => setRowSelection({})}
              onSendAnnouncement={
                canMessage && activeSelectedClients.length > 0
                  ? () => setAnnouncementOpen(true)
                  : undefined
              }
              onAssignTraining={
                canAssignTraining && activeSelectedClients.length > 0
                  ? () => setTrainingDialogOpen(true)
                  : undefined
              }
              onAssignMealPlan={
                activeSelectedClients.length > 0
                  ? () => setMealPlanDialogOpen(true)
                  : undefined
              }
              onAssignCoaches={
                canAssignCoaches && activeSelectedClients.length > 0
                  ? () =>
                      setBulkCoachClientIds(
                        activeSelectedClients.map((client) => client.id),
                      )
                  : undefined
              }
              onArchive={
                archivableSelected.length > 0
                  ? () => setClientsToArchive(archivableSelected)
                  : undefined
              }
            />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
